/**
 * Frecuencia de los "Modales Especiales" (ADV-3347): los modales de
 * lanzamiento, webinars y novedades con los que se comunica la nueva versión
 * (v3) a los admins que todavía están en v2.
 *
 * Hasta este módulo cada modal traía su propia regla, y las reglas divergían:
 * el de lanzamiento y el del webinar de julio se posponían **1 hora** al
 * cerrar; el del webinar del rol Embajador y el de Novedades **insistían en
 * cada carga** hasta el descarte permanente. Esa insistencia era deliberada
 * ("para maximizar el alcance"), pero en la práctica recargar o navegar traía
 * el modal de vuelta, y la única salida era un opt-out para siempre: una
 * decisión mucho más grande que la que quiere tomar alguien que solo quiere
 * seguir trabajando.
 *
 * Ahora la aparición de cualquier Modal Especial está acotada a **una vez cada
 * 7 días por persona**. La misma regla rige en la v3 (`lib/announcements/` de
 * advocatesegc).
 *
 * **Por qué vive acá y no en el archivo de cada modal.** El próximo modal se
 * arma copiando el anterior, y es exactamente así como el "sin snooze" viajó
 * del webinar del rol Embajador a Novedades. Una regla guardada en la config de
 * la campaña se pierde en el próximo copy-paste sin que nadie lo note. La
 * campaña declara SOLO lo suyo (fecha, key, copys); la cadencia es de la
 * familia, igual que la exclusión de empresas de
 * `excludedCompanies.constants.ts`.
 *
 * Qué NO hace: cerrar el modal no cuenta acá. La ventana se abre cuando el
 * modal **aparece** (el Layout llama a `recordSpecialModalShown` en el mismo
 * efecto que lo muestra), así que recargar con el modal abierto tampoco lo trae
 * de vuelta — el criterio del ticket es «una aparición cada 7 días», no «7 días
 * desde que lo cerró». Los descartes permanentes de cada modal (CTA, "No volver
 * a mostrar", flags en DB) siguen siendo suyos y siguen ganando.
 */

/** Ventana entre dos apariciones del mismo modal, para la familia entera. */
export const SPECIAL_MODAL_FREQUENCY_DAYS = 7;

const FREQUENCY_WINDOW_MS = SPECIAL_MODAL_FREQUENCY_DAYS * 24 * 60 * 60 * 1000;

export type SpecialModalUserId = string | number | undefined | null;

const hasUserId = (userId: SpecialModalUserId): boolean =>
  userId !== undefined && userId !== null && userId !== '';

/**
 * Key de la marca de última aparición. Va por modal Y por persona: una empresa
 * puede tener varios admins, y una marca compartida se lo apagaría a todos los
 * compañeros del primero que lo vea.
 */
const lastShownKey = (modalKey: string, userId: SpecialModalUserId): string =>
  `${modalKey}_last_shown:${userId}`;

/**
 * True si este modal ya se le mostró a esta persona dentro de la ventana de
 * frecuencia — o sea, si NO hay que volver a mostrarlo todavía.
 *
 * Fail-closed: sin id de usuario o sin `localStorage` no hay forma de recordar
 * la última aparición, y mostrarlo significaría reaparecer en cada carga, que
 * es justo el bug que este módulo corrige. En esos casos responde `true`.
 */
export const wasSpecialModalShownRecently = (
  modalKey: string,
  userId: SpecialModalUserId
): boolean => {
  if (!hasUserId(userId)) return true;
  try {
    const lastShown = Number(
      localStorage.getItem(lastShownKey(modalKey, userId))
    );
    // Una marca ilegible (`NaN`) o ausente (`0`) cuenta como «nunca se mostró»,
    // y la aparición siguiente la reescribe sana. Una marca en el futuro
    // —reloj corregido hacia atrás— tampoco suprime: si no, el modal quedaría
    // callado tanto tiempo como el desfase del reloj.
    if (!Number.isFinite(lastShown) || lastShown <= 0) return false;
    const elapsed = Date.now() - lastShown;
    return elapsed >= 0 && elapsed < FREQUENCY_WINDOW_MS;
  } catch {
    // localStorage inaccesible (modo privado restrictivo): mejor no mostrarlo
    // que mostrarlo en cada carga sin poder recordar nada.
    return true;
  }
};

/**
 * Abre la ventana de frecuencia para este modal y esta persona. Se llama **al
 * mostrar** el modal, no al cerrarlo.
 */
export const recordSpecialModalShown = (
  modalKey: string,
  userId: SpecialModalUserId
): void => {
  if (!hasUserId(userId)) return;
  try {
    localStorage.setItem(lastShownKey(modalKey, userId), String(Date.now()));
  } catch {
    // Sin almacenamiento disponible no hay nada que persistir.
  }
};
